import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getTranslator } from '../../Translator';
import { canAddParty, getElectionById, getPartiesForElection, isCandidateAlready } from '../../selectors';
import { Link } from 'react-router-dom';
import { NotFound } from '../NotFound';
import { ElectionContainer } from './ElectionContainer';
import { DocumentTitle } from '../DocumentTitle';
import { QuestionMark } from '../ui/QuestionMark';
import { canVote } from '../../Election';
import { PartyItem } from '../party/PartyItem';
import { FilterList } from '../ui/FilterList';
import { PartyState, ElectionStatus, CandidateState } from '../../_generatedTypes.g';
import { CreatePartyPane } from './CreatePartyPane';
import { Translator } from '../../Translator';
import ElectionDetails from './ElectionDetails';
import { partySearched } from '../../features/search/searchSlice';
import { createPartySidePanelToggled } from '../../features/election/electionScreenSlice';
import { useAppSelector } from '../../hooks';

type ElectionComponentParams = {
    electionId: string;
}

export const ElectionComponent = () => {
    const elections = useAppSelector(state => state.elections);
    const parties = useAppSelector(state => state.parties);
    const candidates = useAppSelector(state => state.candidates);
    const language = useAppSelector(state => state.language);
    const searchTermParty = useAppSelector(state => state.searchItemState.SearchTermParty);
    const sidePanelVisible = useAppSelector(state => state.electionScreen.sideMenuVisible);

    const dispatch = useDispatch();
    const t = getTranslator(language);

    const election = getElectionById(elections, Number(useParams<ElectionComponentParams>().electionId));
    if (election.CandidateSystem && !/candidates$/i.test(location.href)) {
        const nwRef = `${location.href.replace(/\/$/, '')}/candidates`;
        location.replace(nwRef);
    }
    const partiesForElection = getPartiesForElection(parties, election.ElectionId);
    const title = t['Parties'];
    const explanation = `${t['Select the']} ${t['party with the candidate']}
            ${t['you want to vote for or click ‘Blank vote’ if you want to issue a blank vote']}`;

    const isAdmin = useAppSelector(state => state.clientState.Account.IsAdmin);

    return !election
        ? <NotFound />
        : <ElectionContainer electionId={election.ElectionId}>
            <div key={election.ElectionId} id={`Election-${election.ElectionId}`}>
                <DocumentTitle title={t.translate(election.Name)} />
                <QuestionMark>{election.ElectionStatus == ElectionStatus.CandidacyPeriod ?
                    <div className="popup-help">
                        <h2>{t.translate({ NL: 'Kandidaatstelling/verkiezing vullen:', EN: 'Fill in your candidacy/election form:' })}</h2>
                        <ul>
                            <li>{t.translate({ NL: 'Klik op ‘partij toevoegen’ om een partij aan te maken.', EN: 'Click on ‘add party’ to create a party.' })}</li>
                            <li>{t.translate({ NL: 'Vul bij naam NL en ENG de naam van de partij in.', EN: 'Enter the name of the party under the name NL and ENG' })}</li>
                            <li>{t.translate({ NL: 'Maak een keuze in lijstvolgorde of meerderheid van stemmen.', EN: 'Make a choice in list order or majority vote.' })}</li>
                            <li>{t.translate({ NL: 'Klik op aanmaken.', EN: 'Click create.' })}</li>
                            <li>{t.translate({ NL: 'Klik op de partij om kandidaten toe te voegen .', EN: 'Click on the party to add candidates.' })}</li>
                        </ul>
                    </div>
                    :
                    <div className="popup-help">
                        <h2>{t.translate({ NL: 'Stemmen:', EN: 'Vote' })}</h2>
                        <p>{explanation}</p>
                    </div>
                }
                </QuestionMark>
                <ElectionDetails election={election} t={t} isAdmin={isAdmin} />
                {election.ElectionStatus == ElectionStatus.CandidacyPeriod && (canAddParty(parties, election.ElectionId) || isCandidateAlready(candidates, election.ElectionId)) &&
                    <div className="election__CandidacyExplanation">
                        <h4>
                            {t.translate({ NL: 'Kandidaatstelling uitleg:', EN: 'Candidacy explanation.' })}
                        </h4>
                        <ul>
                            {canAddParty(parties, election.ElectionId) && <li>
                                {t.translate({
                                    NL: 'Om een nieuwe partij toe te voegen klik op de knop "Voeg mijn partij toe..." . Zie voor meer uitleg over aanmaken partij de tekst onder het help-vraagtekentje bovenin',
                                    EN: 'To add a new game click on the "Add my party..." button. For more information about creating a party, see the text under the help question mark at the top.'
                                })}
                            </li>
                            }
                            {isCandidateAlready(candidates, election.ElectionId) && <li>
                                {t.translate({
                                    NL: 'Om je kandidaatstelling goed te keuren, klik op je partij en klik vervolgens op je eigen naam en dan op de knop "Keur mijn kandidaatstelling goed" .',
                                    EN: 'To approve your candidacy, click on your party and then click on your own name and then on the "Approve my candidacy" button.'
                                })}
                            </li>
                            }
                        </ul>
                    </div>
                }
                {partiesForElection.length
                    ? <div>
                        <QuestionMark>
                            {t.translate({ NL: 'Om een partij te zoeken, voer een deel van de naam in.', EN: 'To search for a party, enter part of the name.' })}
                        </QuestionMark>
                        <h2>{title}</h2>
                        <FilterList<PartyState>
                            searchTerm={searchTermParty}
                            onChange={p => dispatch(partySearched(p))}
                            items={partiesForElection}
                            className="election__parties"
                            label={t['Search party/candidate']}
                            filter={(party, searchTerm) => doesPartyOrCandidateMatchText(party, candidates, searchTerm, t)}
                            render={party => <PartyItem key={party.PartyId} partyId={party.PartyId} />}
                            language={t.languageCode}
                        />
                    </div>
                    : <p>{t['No parties have been registered for this election yet.']}</p>}
                <div className="buttonrow">
                    {canVote(election)
                        ? <Link className="linkbutton" id={`Election-${election.ElectionId}-BlankVote`} to={`/blankvote/${election.ElectionId}`}>{t['Blank vote']}</Link>
                        : <span className="linkbutton linkbutton--disabled" id={`Election-${election.ElectionId}-BlankVote`}>{t['Blank vote']}</span>}
                    <Link className="linkbutton" to="/">{t['Back']}</Link>
                    {(isAdmin && (election.ElectionStatus === ElectionStatus.NotYetOpen || election.ElectionStatus === ElectionStatus.CandidacyPeriod)) && <button className="home__addbutton" onClick={() => dispatch(createPartySidePanelToggled())}>{t['Add Parties']}…</button>}
                    {(!isAdmin && canAddParty(parties, election.ElectionId) && election.ElectionStatus === ElectionStatus.CandidacyPeriod) && <button className="home__addbutton" onClick={() => dispatch(createPartySidePanelToggled())}>{t['Add My Party']}…</button>}
                </div>
            </div>
            {(isAdmin || election.ElectionStatus === ElectionStatus.CandidacyPeriod) && <CreatePartyPane candidacyMode={!isAdmin && election.ElectionStatus === ElectionStatus.CandidacyPeriod} electionId={election.ElectionId} visible={sidePanelVisible} />}
        </ElectionContainer>
}

const doesPartyOrCandidateMatchText = (party: PartyState, candidates: CandidateState[], searchTerm: string, t: Translator) => {
    var candidateFound = candidates.filter(c => c.PartyId === party.PartyId && c.Name.toLowerCase()
        .indexOf(searchTerm.toLowerCase()) !== -1).length > 0;

    var partyFound = t.translate(party.Name)
        .toLowerCase()
        .indexOf(searchTerm.toLowerCase()) !== -1;

    return candidateFound || partyFound;
}

